import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import PanelCard from '../../performance-operations/components/PanelCard';
import {
  Badge, Footer, State, Sub, Table,
} from '../../performance-operations/styles';
import { usePanel } from '../../performance-operations/config';
import Button from '../../../components/ui/Button';
import ConfirmDialog from '../../../components/ui/ConfirmDialog';
import GenericFormDrawer from '../../../components/GenericFormDrawer';
import OptionsMenu from '../../../components/ui/OptionsMenu';
import { formatDate } from '../../../utils/Utils';
import {
  REQUIREMENT_PAGE, addRequirements, applyLowBandRule, employeeOptions, fetchRequirements, removeRequirement,
} from '../config';

// Analiz §24 M33 — "plan gerekli" işaretleri (M33 paydası). Plan ihtiyacı insan (İK ya da doğrudan yönetici) ya da İK'nın açıkça
// uyguladığı kural (dönemin finali düşük bantta olanlar) belirler; tüm çalışanlar zorunlu varsayılmaz. İşaretleme
// GenericFormDrawer ile; kaldırma ve kural uygulama ConfirmDialog ile. Kapsam dışı kişi sunucuda reddedilir (403, kısmi yazım yok).
export default function RequirementsPanel({ filters, isHr, onChanged }) {
  const { t, i18n } = useTranslation();
  const [page, setPage] = useState(1);
  const [refresh, setRefresh] = useState(0);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [removeTarget, setRemoveTarget] = useState(null);
  const [ruleOpen, setRuleOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  const key = JSON.stringify(filters);
  const panel = usePanel(() => fetchRequirements(filters, page), [key, page, refresh], !!filters.periodId);
  const list = panel.data?.rows || [];
  const total = panel.data?.total || 0;
  const pages = Math.max(1, Math.ceil(total / REQUIREMENT_PAGE));
  const changed = () => { setRefresh((r) => r + 1); onChanged?.(); };

  const lookup = useMemo(() => employeeOptions(filters, isHr), [key, isHr]); // eslint-disable-line react-hooks/exhaustive-deps
  const schema = useMemo(() => [
    {
      name: 'employeeIds', label: t('performance.developmentAnalytics.requirement.employees'), type: 'asyncSelect',
      isMulti: true, required: true, grid: 12, minChars: 0, ...lookup,
    },
    { name: 'reason', label: t('performance.developmentAnalytics.requirement.reason'), type: 'textarea', grid: 12, maxLength: 1000 },
    { name: 'scopeNotice', type: 'notice', grid: 12, content: t('performance.developmentAnalytics.requirement.scopeNotice') },
  ], [t, lookup]);

  return (
    <PanelCard
      panelKey="requirements" title={t('performance.developmentAnalytics.panel.requirements')} span={8}
      loading={panel.loading} error={panel.error} onRetry={panel.retry}
      actions={(
        <>
          {isHr ? (
            <Button variant="outline" onClick={() => setRuleOpen(true)} data-action="apply-rule">
              {t('performance.developmentAnalytics.requirement.applyRule')}
            </Button>
          ) : null}
          <Button onClick={() => setDrawerOpen(true)} data-action="mark">{t('performance.developmentAnalytics.requirement.mark')}</Button>
        </>
      )}
    >
      {notice ? <Sub data-note="requirement-result">{notice}</Sub> : null}
      {/* Boş durum burada çizilir (PanelCard'ın empty'si değil): çekmece ve onay diyalogları her durumda bağlı kalmalı. */}
      {list.length === 0 ? <State data-state="empty">{t('performance.developmentAnalytics.requirement.empty')}</State> : (
        <Table>
          <thead>
            <tr>
              <th>{t('performance.developmentAnalytics.col.employee')}</th>
              <th>{t('performance.developmentAnalytics.col.source')}</th>
              <th>{t('performance.developmentAnalytics.col.reason')}</th>
              <th>{t('performance.developmentAnalytics.col.activePlan')}</th>
              <th>{t('performance.developmentAnalytics.col.markedAt')}</th>
              <th aria-label={t('performance.developmentAnalytics.col.actions')} />
            </tr>
          </thead>
          <tbody>
            {list.map((r) => (
              <tr key={r.id} data-row={r.id}>
                <td>{r.employeeName}</td>
                <td>{t(`performance.developmentAnalytics.requirement.source.${r.source}`, { defaultValue: r.source })}</td>
                <td title={r.reason || ''}>{r.reason || '-'}</td>
                <td>
                  {r.hasActivePlan
                    ? <Badge $tone="success">{t('performance.developmentAnalytics.requirement.hasPlan')}</Badge>
                    : <Badge $tone="danger">{t('performance.developmentAnalytics.requirement.noPlan')}</Badge>}
                </td>
                <td>{formatDate(r.createDate, i18n.language)}</td>
                <td>
                  <OptionsMenu row={r} actions={[{ type: 'remove', label: t('performance.developmentAnalytics.requirement.remove'), onClick: () => setRemoveTarget(r) }]} />
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
      {pages > 1 ? (
        <Footer>
          <Button variant="ghost" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-action="prev">{t('dataTable.previous')}</Button>
          <Sub>{t('performance.developmentAnalytics.requirement.page', { page, pages })}</Sub>
          <Button variant="ghost" disabled={page >= pages} onClick={() => setPage((p) => p + 1)} data-action="next">{t('dataTable.next')}</Button>
        </Footer>
      ) : null}

      <GenericFormDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={t('performance.developmentAnalytics.requirement.mark')}
        schema={schema}
        submitLabel={t('performance.developmentAnalytics.requirement.markSubmit')}
        onSave={async (values) => {
          const result = await addRequirements(filters.periodId, values.employeeIds || [], values.reason);
          if (!result) return false;
          setNotice(t('performance.developmentAnalytics.requirement.marked', { added: result.added, skipped: result.skipped || 0 }));
          changed();
          return true;
        }}
      />
      <ConfirmDialog
        isOpen={!!removeTarget}
        title={t('performance.developmentAnalytics.requirement.removeTitle')}
        message={removeTarget ? t('performance.developmentAnalytics.requirement.removeMessage', { name: removeTarget.employeeName }) : ''}
        confirmText={t('performance.developmentAnalytics.requirement.remove')}
        loading={busy}
        onCancel={() => setRemoveTarget(null)}
        onConfirm={async () => {
          setBusy(true);
          const done = await removeRequirement(removeTarget.id);
          setBusy(false);
          setRemoveTarget(null);
          if (done) changed();
        }}
      />
      <ConfirmDialog
        isOpen={ruleOpen}
        type="info"
        title={t('performance.developmentAnalytics.requirement.ruleTitle')}
        message={t('performance.developmentAnalytics.requirement.ruleMessage')}
        confirmText={t('performance.developmentAnalytics.requirement.applyRule')}
        loading={busy}
        onCancel={() => setRuleOpen(false)}
        onConfirm={async () => {
          setBusy(true);
          const result = await applyLowBandRule(filters.periodId);
          setBusy(false);
          setRuleOpen(false);
          if (result) {
            setNotice(t('performance.developmentAnalytics.requirement.ruleApplied', { added: result.added }));
            changed();
          }
        }}
      />
    </PanelCard>
  );
}
